import React, { useEffect } from "react";
import logo from "../img/enjuggernaut_logo.png";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../store";
import AccountInfoPanel from "./AccountInfoPanel";
import { useAuthToken } from "../AuthProvider";
import { TopBar } from "../Topbar";
import { Sidebar } from "../Sidebar";
import { toast } from "react-toastify";
import { Drawer } from "@mui/material";

export function AccountInfo() {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [membershipStatus, setMembershipStatus] = React.useState("FREE");
  const [resumeCredits, setResumeCredits] = React.useState(0);
  const [jobCredits, setJobCredits] = React.useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOverlayOpen, setIsOverlayOpen] = React.useState(false);
  const [isDeleteOverlayOpen, setIsDeleteOverlayOpen] = React.useState(false);
  const [activeSince, setActiveSince] = React.useState("");
  const [activeUntil, setActiveUntil] = React.useState("");
  const [canceled, setCanceled] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(false);
  const userInfo = useSelector((state: AppState) => state.user);
  const { isAuthenticated, token, isLoading, updateToken, isConfirmed } =
    useAuthToken();
  const modalRef = React.useRef<HTMLDialogElement>(null);

  const navigate = useNavigate();

  const updateRedirectUri = (uri: string) => {
    dispatch({
      type: "UPDATE_URI",
      payload: uri,
    });
  };

  async function getAccountInfo() {
    if (isLoading) {
      return;
    }

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    if (!isAuthenticated) {
      updateRedirectUri("/accountinfo");
      navigate("/login");
    } else {
      updateRedirectUri("");

      const apiEndpoint =  "/api/accountinfo";

      try {
        // Make a POST request using fetch
        const response = await fetch(apiEndpoint, {
          method: "GET",
          headers: headers,
        });

        if (response.ok) {
          const responseData = await response.json();
          setResumeCredits(responseData.resume_credits);
          setJobCredits(responseData.job_credits);
          setMembershipStatus(responseData.membership_status);
          setEmail(responseData.email);
          setActiveSince(responseData.active_since);
          setActiveUntil(responseData.active_until);
          setCanceled(responseData.canceled);
          setConfirmed(responseData.confirmed);
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("An error occured.");
      }
    }
  }

  async function deleteAccount() {
    if (isLoading) {
      return;
    }

    const canceled = await cancelMembership();

    if (!canceled && membershipStatus.toUpperCase() == "ACTIVE") {
      console.log("Error cancelling membership.");
      return;
    }

    const apiEndpoint =  "/api/accountinfo";

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    try {
      await fetch(apiEndpoint, {
        method: "DELETE",
        headers: headers,
      });

      navigate("/");
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occured.");
    }
  }

  async function submitSession(session_id: string) {
    try {
      const apiEndpoint = "/api/subscription?session_id=" + session_id;

      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: headers,
      });

      if (response.ok) {
        const responseData = await response.json();

        setSearchParams([]);
      }
    } catch (error) {
      console.log(error);
      toast.error("An error occured.");
    }
  }

  async function submitConfirmation() {
    if (searchParams.has("confirmation_id")) {
      const apiEndpoint =
        
        "/api/confirm?confirmation_id=" +
        searchParams.get("confirmation_id");

      try {
        const headers = {
          Authorization: `Bearer ${token}`,
        };

        const response = await fetch(apiEndpoint, {
          method: "POST",
          headers: headers,
        });

        if (response.ok) {
          toast.success("Email confirmed successfully.");
          setSearchParams([]);
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("An error occured.");
      }
    }
  }

  async function cancelMembership() {
    try {
      const apiEndpoint =  "/api/subscription";

      const headers = {
        Authorization: `Bearer ${token}`,
      };

      await fetch(apiEndpoint, {
        method: "DELETE",
        headers: headers,
      });

      getAccountInfo();

      setIsOverlayOpen(false);
      return true;
    } catch (error) {
      console.log(error);
      toast.error("An error occured.");
      return false;
    }
  }

  async function resendConfirmation() {
    try {
      const apiEndpoint =  "/api/resend";

      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: headers,
      });

      if (response.ok) {
        toast.success("Confirmation email sent.");
      } else {
        const responseData = await response.json();
        toast.error(responseData.error);
      }
    } catch (error) {
      console.log(error);
      toast.error("An error occured.");
    }
  }

  useEffect(() => {
    if (searchParams.has("confirmation_id")) {
      submitConfirmation();
      navigate("/login");
    } else if (!isAuthenticated && !isLoading) {
      updateRedirectUri("/account");
      navigate("/login");
    } else if (!isLoading) {
      updateRedirectUri("");
      getAccountInfo();

      const maybe_session_id = searchParams.get("session_id");

      if (maybe_session_id) {
        submitSession(maybe_session_id);
      }
    }
  }, [userInfo.resume_uuid, isAuthenticated, isLoading, searchParams]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  if (isLoading) {
    return <div className="dark-panel"></div>;
  }

  modalRef.current?.showModal();

  return (
    <div>
      <header className="header dark-panel visible sticky top-0 z-20 mx-auto flex flex-row items-center justify-between p-2 lg:hidden lg:p-4">
        <img src={logo} className="h-auto w-1/2" alt="logo" />

        <div>
          <button
            onClick={toggleMenu}
            className="text-white hover:text-gray-300 focus:outline-none"
          >
            <svg
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="h-12 w-12"
            >
              {isOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </header>

      {isOverlayOpen && (
        <div className="overlay">
          <div className="agreement-content w-1/2">
            <h2 className="text-center font-code md:text-lg lg:text-xl xl:text-2xl ">
              Are you sure you want to cancel?
            </h2>

            <div className="flex w-full flex-row pt-4 lg:pt-6">
              <button
                className="start-button mx-auto px-4 py-2 font-bold md:text-lg lg:text-xl xl:text-2xl"
                onClick={() => cancelMembership()}
              >
                Yes, I&apos;m sure
              </button>
              <button
                className="login-button mx-auto px-4 py-2 font-bold md:text-lg lg:text-xl xl:text-2xl"
                onClick={() => setIsOverlayOpen(false)}
              >
                Nevermind
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteOverlayOpen && (
        <div className="overlay">
          <div className="agreement-content w-1/2">
            <h2 className="text-center font-code md:text-lg lg:text-xl xl:text-2xl ">
              Are you sure you want to{" "}
              <span className="font-bold">DELETE YOUR ACCOUNT</span>?
            </h2>

            <div className="flex w-full flex-row pt-4 lg:pt-6">
              <button
                className="start-button mx-auto px-4 py-2 font-bold md:text-lg lg:text-xl xl:text-2xl"
                onClick={() => deleteAccount()}
              >
                Yes, I&apos;m sure
              </button>
              <button
                className="login-button mx-auto px-4 py-2 font-bold md:text-lg lg:text-xl xl:text-2xl"
                onClick={() => setIsDeleteOverlayOpen(false)}
              >
                Nevermind
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex min-h-screen max-w-full flex-row">
        <div className="light-panel flex w-full flex-col lg:w-4/5 xl:w-5/6">
          <div className="px-8 py-8 lg:px-16 lg:py-16">
            <div className="dark-panel mt-4 flex flex-col justify-start rounded-xl">
              <div className="flex flex-col justify-between px-6 md:flex-row lg:px-12 xl:px-20 2xl:px-24">
                {confirmed && (
                  <h1 className="light-text py-4 font-code font-bold md:text-2xl lg:text-4xl">
                    EMAIL
                  </h1>
                )}

                {!confirmed && (
                  <h1 className="light-text py-4 font-code font-bold md:text-2xl lg:text-4xl">
                    EMAIL{" "}
                    <span className="text-red-500 md:text-lg lg:text-2xl">
                      (not confirmed)
                    </span>
                  </h1>
                )}
                <h1 className="light-text py-4 font-code md:text-2xl lg:text-4xl">
                  {email.toUpperCase()}
                </h1>
              </div>
            </div>
            <AccountInfoPanel
              header={"RESUME CREDITS"}
              text={`${resumeCredits}`}
            />
            <AccountInfoPanel header={"JOB CREDITS"} text={`${jobCredits}`} />
            <div className="dark-panel mt-4 flex flex-col rounded-xl">
              <AccountInfoPanel
                header={"CONFIRMATION STATUS"}
                text={`${confirmed ? "CONFIRMED" : "NOT CONFIRMED"}`}
              />
              <div className="ml-auto px-6 lg:px-12 xl:px-20 2xl:px-24">
                {!confirmed && (
                  <button
                    onClick={() => resendConfirmation()}
                    className="start-button my-2 ml-auto px-4 py-2 font-bold md:text-lg lg:text-xl xl:text-2xl"
                  >
                    RESEND CONFIRMATION EMAIL
                  </button>
                )}
              </div>
            </div>
            <div className="dark-panel mt-4 flex flex-col justify-start rounded-xl">
              <div className="flex flex-col px-6 lg:px-12 xl:px-20 2xl:px-24">
                <div className="flex flex-col justify-between md:flex-row">
                  <h1 className="light-text py-4 font-code font-bold md:text-2xl lg:text-4xl">
                    MEMBERSHIP STATUS
                  </h1>
                  <h1 className="light-text py-4 font-code md:text-right md:text-2xl lg:text-4xl">
                    {membershipStatus.toUpperCase()}
                  </h1>
                </div>
                <div className="flex flex-col">
                  {membershipStatus.toUpperCase() == "ACTIVE" && (
                    <div className="flex flex-col justify-between md:flex-row">
                      <h1 className="light-text py-4 font-code md:text-2xl lg:text-4xl">
                        ACTIVE SINCE: {activeSince}
                      </h1>
                      {!canceled && (
                        <button
                          onClick={() => setIsOverlayOpen(true)}
                          className="login-button my-2 w-full py-2 font-bold md:ml-auto md:w-1/6 md:text-lg lg:text-xl xl:text-2xl"
                        >
                          CANCEL
                        </button>
                      )}
                      {canceled && (
                        <h1 className="light-text py-4 font-code md:text-2xl lg:text-4xl">
                          ENDS AT: {activeUntil}
                        </h1>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="dark-panel mt-4 flex flex-col justify-start rounded-xl">
              <div className="flex flex-col justify-between px-6 py-2 md:flex-row lg:px-12 xl:px-20 2xl:px-24">
                <h1 className="light-text py-4 font-code font-bold md:text-2xl lg:text-4xl">
                  DELETE ACCOUNT
                </h1>
                <button
                  onClick={() => setIsDeleteOverlayOpen(true)}
                  className="login-button my-2 w-full py-2 font-bold md:w-1/6 md:text-lg lg:text-xl xl:text-2xl"
                >
                  DELETE
                </button>
              </div>
            </div>
          </div>
        </div>
        <Drawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)}>
          <Sidebar minimized={false} drawer={true} />
        </Drawer>
        <Sidebar minimized={false} drawer={false} />
      </div>
    </div>
  );
}
